'use client';

type PreconnectItem = { host: string; crossOrigin?: '' | 'anonymous' };

type Props = {
  assetsBasePath?: string | null; // e.g., https://webfont-dev.amcom-corp-preprod.amway.net
  adaScriptSource?: string | null; // e.g., https://static.ada.support/embed2.js
  adaHandle?: string | null; // non-URL handle; if present we include ADA host

  extraPreconnect?: PreconnectItem[]; // optional extras for later
  extraDnsPrefetch?: string[]; // optional extras for later
};

const toHost = (url?: string | null) => {
  try {
    if (!url) {
      return null;
    }
    if (url.startsWith('//')) {
      return new URL(`https:${url}`).host;
    }
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return new URL(url).host;
    }
    return null; // relative path → same origin
  } catch {
    return null;
  }
};

export default function PerfResourceHints({
  assetsBasePath,
  adaScriptSource,
  adaHandle,
  extraPreconnect = [],
  extraDnsPrefetch = [],
}: Props) {
  // dynamic hosts
  const dynPreconnect: PreconnectItem[] = [];
  const assetsHost = toHost(assetsBasePath);
  if (assetsHost) {
    dynPreconnect.push({ host: assetsHost, crossOrigin: 'anonymous' });
  } // fonts usually need CORS

  const adaHost = toHost(adaScriptSource);
  if (adaHost && adaHandle) {
    dynPreconnect.push({ host: adaHost, crossOrigin: 'anonymous' });
  }

  // merge & de-dup preconnect
  const seen = new Set<string>();
  const preconnectAll = [...extraPreconnect, ...dynPreconnect].filter(({ host }) => {
    if (!host || seen.has(host)) {
      return false;
    }
    seen.add(host);
    return true;
  });

  // de-dup dns-prefetch and avoid ones already preconnected
  const dnsPrefetchAll = Array.from(new Set((extraDnsPrefetch || []).filter((h) => h && !seen.has(h))));

  return (
    <>
      <meta httpEquiv="x-dns-prefetch-control" content="on" />
      {preconnectAll.map(({ host, crossOrigin }) => (
        <link
          key={`preconnect:${host}`}
          rel="preconnect"
          href={`https://${host}`}
          {...(crossOrigin ? { crossOrigin } : {})}
        />
      ))}
      {dnsPrefetchAll.map((host) => (
        <link key={`dns:${host}`} rel="dns-prefetch" href={`//${host}`} />
      ))}
    </>
  );
}
